import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Breadcrumbs, Button, Checkbox, CircularProgress,
  FormControl, InputLabel, MenuItem, Select,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';
import AppShell from './components/AppShell';
import api from './services/api';
import DisqualifyStatus from './disqualify/DisqualifyStatus';
import useDisqualify from './disqualify/useDisqualify';

const AIDetection = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([null]);
  const [aiIDs, setAiIDs] = useState([]);
  const [nAI, setNAI] = useState(0);
  const [aiAnswers, setAiAnswers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDisqualified, setIsDisqualified] = useState(false);
  const disq = useDisqualify(surveyId);
  const [loadingCheck, setLoadingCheck] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => {
      setSurvey(res.data);
      api.get('/alchemer/questions/', { params: { survey_id: res.data.alchemer_survey_id } })
        .then((qRes) => setQuestions(qRes.data.data.map((q) => ({ id: q.id, title: q.title.English }))));
    });
  }, [surveyId]);

  const handleSubmit = async () => {
    setLoadingCheck(true);
    const res = await api.post(`/surveys/${surveyId}/ai-detection/`, { question1: selectedQuestions[0] });
    setAiIDs(res.data.AI_IDs);
    setNAI(res.data.n_AI);
    setAiAnswers(res.data.AI_answers);
    setLoadingCheck(false);
  };

  const handleDisqualify = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { alert('No responses selected.'); return; }
    setIsDisqualified(true);
    await disq.run(ids);
    setIsDisqualified(false);
  };

  const handleDownloadCSV = () => {
    if (!aiIDs.length) { alert('No AI responses to download.'); return; }
    const csv = 'data:text/csv;charset=utf-8,SurveyID\n' + aiIDs.join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', 'ai_responses.csv');
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const toggleId = (id) => setSelectedIds((prev) => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  if (!survey) return null;

  return (
    <AppShell maxWidth="md">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Link to={`/survey/${surveyId}/text-analysis`} style={{ textDecoration: 'none', color: 'inherit' }}>Open Text Analysis</Link>
        <Typography color="textPrimary">AI Detection</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <SmartToyRoundedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          {isDisqualified && (
            <DoNotDisturbAltRoundedIcon sx={{ fontSize: 28, color: 'error.main', position: 'absolute', bottom: -4, right: -4 }} />
          )}
        </Box>
        <Box>
          <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>AI Detection</Typography>
          <Typography color="text.secondary">Select a question to check for AI-generated responses.</Typography>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: 3,
        }}
      >
        {selectedQuestions.map((_, index) => (
          <FormControl key={index} fullWidth>
            <InputLabel>Question {index + 1}</InputLabel>
            <Select
              value={selectedQuestions[index] || ''}
              label={`Question ${index + 1}`}
              onChange={(e) => { const u = [...selectedQuestions]; u[index] = e.target.value; setSelectedQuestions(u); }}
            >
              <MenuItem value=""><em>Select a question</em></MenuItem>
              {questions.map((q) => <MenuItem key={q.id} value={q.id}>{q.title}</MenuItem>)}
            </Select>
          </FormControl>
        ))}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="contained" onClick={handleSubmit} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Check for AI
          </Button>
          {loadingCheck && <CircularProgress size={24} />}
        </Box>
      </Box>

      {aiIDs.length > 0 && (
        <Box
          sx={{
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 3,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>AI responses: {nAI}</Typography>
          <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                  <TableCell sx={{ width: '15%', fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ width: '65%', fontWeight: 'bold' }}>Text</TableCell>
                  <TableCell sx={{ width: '20%', fontWeight: 'bold' }}>Select</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aiIDs.map((id, i) => (
                  <TableRow key={id}>
                    <TableCell>{id}</TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{aiAnswers[i]}</TableCell>
                    <TableCell><Checkbox onChange={() => toggleId(id)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" color="secondary" onClick={handleDisqualify} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Disqualify Selected
            </Button>
            <Button variant="outlined" onClick={handleDownloadCSV} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Download CSV
            </Button>
          </Box>
          <DisqualifyStatus state={disq} surveyId={surveyId} />
        </Box>
      )}
    </AppShell>
  );
};

export default AIDetection;
